import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { authService } from '../services/authService';

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setErrorMessage('');
        setIsLoading(true);

        try {
            await authService.forgotPassword(email);
            setSuccess(true);
        } catch (err: any) {
            // Removed expected error capture
            setErrorMessage(err.message || 'Error al enviar el correo.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="mx-auto flex min-h-screen max-w-md flex-col bg-white shadow-xl">
            <div className="flex flex-1 flex-col justify-center px-6 py-12">
                {success ? (
                    /* Success state */
                    <div className="text-center">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                            <CheckCircle size={32} className="text-green-600" />
                        </div>
                        <h1 className="mb-2 text-2xl font-bold text-gray-900">
                            ¡Correo enviado!
                        </h1>
                        <p className="mb-8 text-sm text-gray-500">
                            Hemos enviado un código de recuperación a <strong className="text-gray-700">{email}</strong>. Revisa tu bandeja de entrada.
                        </p>
                        <Link
                            to={`/reset-password?email=${encodeURIComponent(email)}`}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-600 py-3.5 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition-all hover:bg-indigo-700 active:scale-[0.98]"
                        >
                            Tengo el código
                        </Link>
                        <Link
                            to="/login"
                            className="mt-4 inline-flex items-center justify-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                        >
                            <ArrowLeft size={16} />
                            Volver al login
                        </Link>
                    </div>
                ) : (
                    /* Form state */
                    <>
                        <div className="mb-10 text-center">
                            <h1 className="text-2xl font-bold text-gray-900">
                                Recuperar contraseña
                            </h1>
                            <p className="mt-2 text-sm text-gray-500">
                                Ingresa tu correo electrónico y te enviaremos un código para restablecer tu contraseña.
                            </p>
                        </div>

                        <div className={`mb-6 rounded-2xl bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-600 transition-all ${errorMessage ? 'block' : 'hidden'}`}>
                            {errorMessage || '\u00A0'}
                        </div>

                        <form onSubmit={handleSubmit} translate="no" className="flex flex-col gap-4">
                            <div>
                                <label
                                    htmlFor="forgot-email"
                                    className="mb-1 block text-sm font-medium text-gray-700"
                                >
                                    Correo electrónico
                                </label>
                                <div className="relative">
                                    <Mail
                                        size={18}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                                    />
                                    <input
                                        id="forgot-email"
                                        type="email"
                                        required
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="tu@correo.com"
                                        className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3.5 pl-11 pr-4 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-indigo-400 focus:bg-white focus:ring-2 focus:ring-indigo-100"
                                    />
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl bg-indigo-600 py-3.5 text-sm font-semibold text-white shadow-lg shadow-indigo-200 transition-all hover:bg-indigo-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 size={18} className="animate-spin" />
                                        <span>Enviando...</span>
                                    </>
                                ) : (
                                    <span>Enviar código de recuperación</span>
                                )}
                            </button>
                        </form>

                        <Link
                            to="/login"
                            className="mt-6 inline-flex items-center justify-center gap-1 text-center text-sm font-medium text-indigo-600 hover:text-indigo-700"
                        >
                            <ArrowLeft size={16} />
                            Volver al login
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
